import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Tooltip } from "antd";
import { exchangeOptions } from "constants/constant";

const ConnectExchangeUI = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPaperMode = searchParams.get("mode") === "Paper";
  const [mode, setMode] = useState(isPaperMode ? "Paper" : "Live");
  const [exchanges, setExchanges] = useState([]);

  // Logos
  const logoMap = {
    Binance: "https://api.coinray.eu/images/logos/bina.png",
    Bybit: "https://images.seeklogo.com/logo-png/41/1/bybit-logo-png_seeklogo-412982.png",
    BitMEX: "https://api.coinray.eu/images/logos/bitmex.png",
    CoinDCX: "https://api.coinray.eu/images/logos/coindcx.png",
    Crypto: "https://api.coinray.eu/images/logos/cro.png",
    Coinbase: "https://api.coinray.eu/images/logos/gdax.png",
    Kraken: "https://api.coinray.eu/images/logos/krkn.png",
    KuCoin: "https://api.coinray.eu/images/logos/kucn.png",
    OKX: "https://api.coinray.eu/images/logos/okex.png",
    "Gate.io": "https://api.coinray.eu/images/logos/gate.png",
    MEXC: "https://api.coinray.eu/images/logos/mexc.png",
    Bitget: "https://api.coinray.eu/images/logos/bitget.png",
    Bitfinex: "https://api.coinray.eu/images/logos/bitfinex.png",
    Poloniex: "https://api.coinray.eu/images/logos/plnx.png",
    Huobi: "https://api.coinray.eu/images/logos/hubi.png",
  };

  useEffect(() => {
    // Static exchange data from constants
    const filteredExchanges = exchangeOptions.map((ex) => ({
      name: ex.label,
      logo: logoMap[ex.label] || "",
      maintenance: true, // mark as active
    }));
  
    setExchanges(filteredExchanges);
  }, []);

  const handleBack = () => {
    navigate("/dashboard/api");
  };

  const handleExchangeSelect = (exchange) => {
    if (exchange.maintenance) {
      navigate(`/dashboard/connect/${exchange.name}/${mode}`);
    }
  };

  const renderExchangeCard = (exchange) => {
    const isActive = exchange.maintenance;
    return (
      <motion.div
        key={exchange.name}
        whileHover={isActive ? { scale: 1.05 } : {}}
        className={`rounded-2xl shadow-lg border transition-all cursor-pointer flex flex-col items-center justify-center p-4 
          ${
            isActive
              ? "bg-gray-800/70 hover:bg-gray-700/70 border-gray-600"
              : "bg-gray-900/40 border-gray-700 opacity-50 cursor-not-allowed"
          }`}
        onClick={() => isActive && handleExchangeSelect(exchange)}
      >
        <Tooltip title={!isActive ? "Coming Soon" : ""}>
          <img
            src={exchange.logo}
            alt={exchange.name}
            className="w-12 h-12 object-contain mb-2"
          />
        </Tooltip>
        <p className="text-sm font-medium text-white">{exchange.name}</p>
      </motion.div>
    );
  };

  return (
    <div className="text-white p-4 md:p-6">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="dark:bg-gray-900 border border-gray-700/50 backdrop-blur-xl bg-white/10 p-6 rounded-2xl shadow-xl  mx-auto"
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={handleBack}
              className="p-2 rounded-full bg-Profitslatheme-neonMed transition"
            >
              <ArrowLeft size={20} className="text-white" />
            </button>
            <h2 className="text-xl font-semibold text-Profitslatheme-neonMed">
              {mode === "Live"
                ? "Connect Exchange Account"
                : "Add Paper Trading Account"}
            </h2>
          </div>

          {/* Mode Switch */}
          {!isPaperMode && (
            <div className="flex bg-gray-800 rounded-full overflow-hidden shadow-md">
              {["Paper", "Live"].map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`px-4 py-1 text-sm transition-all ${
                    mode === m
                      ? "bg-primary text-secondary"
                      : "text-white hover:text-white"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info Card */}
        <div className="p-5 bg-gray-800/80 rounded-xl mb-6 border border-gray-700">
          <div className="text-white">
            <div className="flex gap-2 mb-3 items-center uppercase text-lg">
              <ShieldCheck className="text-primary" size={24} />
              <strong>
                {mode === "Live"
                  ? "Live Exchange Trading"
                  : "Paper Trading"}
              </strong>
            </div>
            {mode === "Live" ? (
              <p className="ml-6 text-sm text-gray-300 leading-relaxed">
                {import.meta.env.VITE_PROJECT} encrypts and securely stores
                your API keys with bank-level security. They can only be
                decrypted using your Vault password. Remember, storage outside{" "}
                {import.meta.env.VITE_PROJECT} (like on the exchange or
                locally) may not be encrypted and can be vulnerable.
              </p>
            ) : (
              <p className="ml-6 text-sm text-gray-300 leading-relaxed">
                Practice trading, test strategies, and explore exchanges using {" "}
                {import.meta.env.VITE_PROJECT}'s Paper Trading. You can even
                manually adjust balances to simulate different scenarios.
              </p>
            )}
          </div>
        </div>

        {/* Exchanges Grid */}
        <div>
          <h3 className="text-sm font-semibold mb-4 text-white">
            Supported Exchanges
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {exchanges.map((exchange) => renderExchangeCard(exchange))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ConnectExchangeUI;
